import {useCallback, useMemo} from 'react'
import type {EstablishedAhpSession} from '@github-ui/ahp-session-client/contracts'
import {NullMessageId} from '@github-ui/copilot-chat-types'
import {EstablishedAhpSessionProvider} from '@github-ui/copilot-shared-ui/components/EstablishedAhpSessionProvider'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- the workbench lives in copilot-immersive-v1 until Chat V1 is retired
import {useSafeContentPreviewActions} from '@github-ui/copilot-immersive-v1/components/ContentPreview/ContentPreviewContext'
import type {
  ChangesetDiff,
  PreviewableContentIdentifier,
  // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- see above
} from '@github-ui/copilot-immersive-v1/components/ContentPreview/content-preview-types'

import {DiffWorkbenchContent} from '../components/task-view/DiffWorkbenchContent'
import {DiffWorkbenchTabBadge} from '../components/task-view/DiffWorkbenchTabBadge'
import {SavedTaskChangesetContext} from './use-task-changeset'
import type {TaskHistorySession} from '../utils/replay-task-history-events'

export interface OpenDiffWorkbenchTabArgs {
  taskId: string
  repoNwo: string
  headRef: string
  initialFilePath?: string | null
  session: EstablishedAhpSession | undefined
  saved: TaskHistorySession | undefined
}

export interface UseOpenDiffWorkbenchTabResult {
  /** Registers the tab as open — pinned first, non-closable — without selecting it or forcing
   * the preview pane open. Used to keep the diff tab always present whenever a workbench host
   * is active for a changeset-backed session, regardless of whether anything else is open yet. */
  register: (args: OpenDiffWorkbenchTabArgs) => void
  /** Opens the tab (selecting it, and expanding the preview pane) — used for explicit triggers
   * like the "Changes" button. */
  open: (args: OpenDiffWorkbenchTabArgs) => void
}

function buildItem({taskId, repoNwo, headRef, initialFilePath, session, saved}: OpenDiffWorkbenchTabArgs): {
  id: PreviewableContentIdentifier
  item: ChangesetDiff
} {
  const id: PreviewableContentIdentifier = `changeset-diff:${taskId}`
  return {
    id,
    item: {
      type: 'changeset-diff',
      id,
      messageId: NullMessageId,
      name: 'Files changed',
      pinned: true,
      disableCloseButton: true,
      // `DiffWorkbenchContent` reads the session/changeset via EstablishedAhpSessionContext and
      // SavedTaskChangesetContext, but this tab is rendered by AgentsWorkbenchHost's WorkbenchColumn,
      // a structural sibling of TaskChat (not a descendant) — so those contexts don't reach it from
      // TaskChat's own providers. Re-provide them here using the values captured at call time.
      trailingComponent: (
        <SavedTaskChangesetContext value={saved}>
          <EstablishedAhpSessionProvider session={session}>
            <DiffWorkbenchTabBadge />
          </EstablishedAhpSessionProvider>
        </SavedTaskChangesetContext>
      ),
      renderPreview: () => (
        <SavedTaskChangesetContext value={saved}>
          <EstablishedAhpSessionProvider session={session}>
            <DiffWorkbenchContent repoNwo={repoNwo} headRef={headRef} initialFilePath={initialFilePath} />
          </EstablishedAhpSessionProvider>
        </SavedTaskChangesetContext>
      ),
    },
  }
}

/**
 * Opens the changeset diff as a tab in the ambient `AgentsWorkbenchHost`, when one is active
 * (Mission Control Task page, managed-sandbox sessions). Returns `undefined` when there's no
 * host to open a tab in — callers should fall back to `DiffOverlay` in that case.
 */
export function useOpenDiffWorkbenchTab(): UseOpenDiffWorkbenchTabResult | undefined {
  // `useSafeContentPreviewActions()` (not the reactive `ContentPreviewContext`, which changes
  // identity whenever items/openItems/selectedItem change) so calling `register()` in an effect
  // doesn't retrigger that same effect on every call — its stable, action-only context never
  // changes identity. Also safe to call when no `ContentPreviewProvider` ancestor exists.
  const contentPreview = useSafeContentPreviewActions()

  const register = useCallback(
    (args: OpenDiffWorkbenchTabArgs) => {
      if (!contentPreview) return
      const {id, item} = buildItem(args)
      contentPreview.updateItem(item)
      contentPreview.openItem(id, false)
    },
    [contentPreview],
  )

  const open = useCallback(
    (args: OpenDiffWorkbenchTabArgs) => {
      if (!contentPreview) return
      const {id, item} = buildItem(args)
      contentPreview.updateItem(item)
      contentPreview.openItem(id)
      contentPreview.openPreviewPane()
    },
    [contentPreview],
  )

  return useMemo(() => (contentPreview ? {register, open} : undefined), [contentPreview, register, open])
}
